import { AfterViewInit, Component, Input, OnDestroy, Renderer2 } from '@angular/core';
import { environment } from 'src/environments/environment';
import { IBoardwalkCustomParams } from 'src/app/domain/interfaces/crossell/interface-cross-sell-component';
import { EBoardwalkPage } from 'src/app/domain/models/crossell/boardwalk-page-enum';
import { ScriptsIds } from 'src/app/domain/models/scripts/scripts-ids';
import { ScriptService } from 'src/app/infraestructure/services-views/script-service/script.service';
import { createEmptyBoardwalkParams } from 'src/app/infraestructure/helpers/cross-sell/boardwalk-params.helper';
import { getBoardwalkDebugParams } from 'src/app/infraestructure/helpers/cross-sell/boardwalk-debug-params.helper';

declare global {
  interface Window {
    boardwalkCustomParams?: IBoardwalkCustomParams;
  }
}

@Component({
  selector: 'app-boardwalk-v2',
  templateUrl: './boardwalk-v2.component.html'
})
export class BoardwalkV2Component implements AfterViewInit, OnDestroy {
  @Input() token: string = '';
  @Input() customParams?: IBoardwalkCustomParams;
  @Input() pageType?: EBoardwalkPage | string;
  // Boardwalk requires this id to appear only once per page.
  @Input() outletId: string = 'exboardwalk-path';

  private styleElement?: HTMLLinkElement;
  private paramsSnapshot?: IBoardwalkCustomParams;

  constructor(private scriptService: ScriptService, private renderer: Renderer2) { }

  get scriptId(): string {
    return `${ScriptsIds.BoardwalkV2}-${this.outletId}`;
  }

  // AfterViewInit so the outlet div is already in the DOM when the embed script runs.
  ngAfterViewInit(): void {
    if (!this.token) {
      console.warn('[Boardwalk] V2 embed token is missing; wall not loaded.');
      return;
    }
    this.restoreDebugParamsInUrl();
    // Must be set before the embed script loads: Boardwalk reads it once at init.
    this.paramsSnapshot = { ...createEmptyBoardwalkParams(), ...this.customParams };
    window.boardwalkCustomParams = this.paramsSnapshot;
    this.appendPageStyles();

    const loaderUrl = environment.BoardwalkIntegrationV2.replace('{token}', encodeURIComponent(this.token));
    this.scriptService.loadScript(this.scriptId, loaderUrl, { skipIntegrity: true }).then((result: any) => {
      if (!result?.loaded) {
        console.error('[Boardwalk] V2 embed script failed to load.', loaderUrl);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.styleElement) {
      this.renderer.removeChild(document.head, this.styleElement);
    }
    // Only clear the global if no other wall has replaced it since.
    if (this.paramsSnapshot && window.boardwalkCustomParams === this.paramsSnapshot) {
      window.boardwalkCustomParams = undefined;
    }
    this.scriptService.removeScript(this.scriptId);
  }

  // Boardwalk reads bwtest / bw_debug from the page URL; put them back if navigation dropped them.
  private restoreDebugParamsInUrl(): void {
    const debugParams = getBoardwalkDebugParams();
    const url = new URL(window.location.href);
    let changed = false;
    Object.entries(debugParams).forEach(([key, value]) => {
      if (!url.searchParams.has(key)) {
        url.searchParams.set(key, value);
        changed = true;
      }
    });
    if (changed) {
      window.history.replaceState(window.history.state, '', url.toString());
    }
  }

  private appendPageStyles(): void {
    if (!this.pageType) {
      return;
    }
    this.styleElement = this.renderer.createElement('link');
    this.renderer.setAttribute(this.styleElement, 'rel', 'stylesheet');
    this.renderer.setAttribute(this.styleElement, 'type', 'text/css');
    this.renderer.setAttribute(this.styleElement, 'href', environment.UrlBucketDynamicCSSBoardwalk.replace('{PageType}', this.pageType));
    this.renderer.appendChild(document.head, this.styleElement);
  }
}
